import { add, isAfter, parseISO } from 'date-fns';

import type { DateRange as Range } from '../../../models';
import type { ChangeLogRecordsList } from '../types';
import { getChangeLogTablesProps, getChangesLengthWithoutDuplicates, getChangesPluralString } from '../utils';
import type { FilteredLogs, GetNormalizedChangesProps, TargetingChanges } from './types';

export function countTargetingChanges(targetingChanges: TargetingChanges): number {
  if (!targetingChanges) {
    console.error('countTargetingChanges was called without any targeting changes.');
    return 1;
  }

  const targetingChangesMap = targetingChanges.targetingChangesMap;
  let count = (targetingChangesMap?.old?.termList?.length ?? 0) + (targetingChangesMap?.new?.termList?.length ?? 0);
  if (targetingChangesMap?.old?.termList?.length && targetingChangesMap?.new?.termList?.length) {
    targetingChangesMap?.old?.termList.forEach((oldTerm) => {
      if (targetingChangesMap?.new?.termList?.find((newTerm) => newTerm?.dimension === oldTerm?.dimension)) {
        // key exists on both sides and counts as one change, not two.
        count--;
      }
    });
  }
  return count;
}

export const isStartDateAfterEndDate = (dateRange: Range): boolean => {
  const startDate = new Date(dateRange.startDate);

  if (!dateRange.startDate) return true;

  if (dateRange.endDate) {
    return isAfter(startDate, new Date(dateRange.endDate));
  }

  return isAfter(startDate, new Date());
};

/**
 * Converts a local date (YYYY-MM-DD) to its UTC equivalent at the start or end of the local day.
 *
 * @param date - The date string in 'YYYY-MM-DD' format (user's local timezone).
 * @returns UTC date-time string in ISO format (without milliseconds).
 */
export const convertLocalDateToUTC = (date: string): string => {
  const parsedDate = parseISO(date);

  const localTimeOffSet = parsedDate.getTimezoneOffset();

  const utcDate = add(parsedDate, {
    minutes: localTimeOffSet,
  });

  // Format as ISO string without milliseconds
  return utcDate.toISOString().split('.')[0] + 'Z';
};

export const getNormalizedChanges = ({
  previousObject,
  nestedChangesPrefix,
  previousDayPartList,
  node,
  entityType,
  targetingChanges,
}: GetNormalizedChangesProps): FilteredLogs => {
  const tablesProps = getChangeLogTablesProps({
    previousObject,
    nestedChangesPrefix,
    previousDayPartList,
    node,
    entityType,
  });

  let changesLength = getChangesLengthWithoutDuplicates(
    tablesProps.auditProps.reduce<ChangeLogRecordsList>((acc, { changes }) => [...acc, ...changes], [])
  );

  const creativeChanges = node.creativeAudits.length > 0 ? node.creativeAudits[0].changeList : [];

  let creativeChangesLength = getChangesLengthWithoutDuplicates(
    tablesProps.creativeProps[0]?.changes! ? tablesProps.creativeProps[0].changes! : creativeChanges!
  );

  if (targetingChanges) {
    const targetingChangesLength = countTargetingChanges(targetingChanges);
    if (targetingChangesLength) {
      changesLength -= 1;
      changesLength += targetingChangesLength;
    }
  }

  const changesPlural = getChangesPluralString(changesLength);
  const creativeChangesPlural = getChangesPluralString(creativeChangesLength);

  return {
    audits: {
      tablesProps: tablesProps.auditProps,
      changesPlural,
      changesLength,
    },
    creativeAudits: {
      tablesProps: tablesProps.creativeProps,
      changesPlural: creativeChangesPlural,
      changesLength: creativeChangesLength,
    },
  };
};
